// @flow

import { type FileType } from '@ta/input.drop-zone';

export type MediaType = 'photo' | 'video' | 'unknown';

/**
 * A type that represents a size (cut) of a media.
 * Most media will have different cuts
 */
export type MediaSize = {|
  /** width of the cut */
  width: number,

  /** height of the cut */
  height: number,

  /** url of the cut */
  url: string,
|};

/**
 * Information about a media.
 */
export type PhotoInfo = {|
  /** media id */
  id: number,

  /** array of sizes, cuts */
  photoSizes: Array<MediaSize>,
|};

/**
 * A type that represents the output of cropping image
 */
export type CropOutput = {|
  /** portion of the image that is cropped */
  dataUrl: string,
|};

/**
 * A geographical point that can be used to query locations.
 */
export type GeoPoint = {|
  latitude: number,
  longitude: number,
|};

/**
 * The types of images we support for upload
 * Note: .jpeg is important because it is what iOS 11 provides
 */
export const supportedImageTypes: Array<FileType> = ['.jpg', '.jpeg', '.gif', '.png'];

/**
 * The types of video we support for upload
 */
export const supportedVideoTypes: Array<FileType> = ['.mp4', '.m4v', '.mov'];

/**
 * Find the size that has the most pixels
 */
export const getSizeWithMostPixels = (sizes: Array<MediaSize>): ?MediaSize => {
  let bestYet: MediaSize;
  sizes.forEach(size => {
    if (!bestYet) {
      bestYet = size;
    } else if (bestYet.width * bestYet.height < size.width * size.height) {
      bestYet = size;
    }
  });
  return bestYet;
};

/**
 * A simple point class to manage x and y together in a single object.
 * This class also helps with 2D operations.
 */
export class Point {
  x: number;
  y: number;

  constructor(x: number, y: number) {
    this.x = x;
    this.y = y;
  }

  distanceFrom = (other: Point): number =>
    Math.sqrt((this.x - other.x) ** 2 + (this.y - other.y) ** 2);
}

/**
 * A simple dimension class to manage width and height together in a single object.
 * This class also helps with 2D operations.
 */
export class Dimension {
  width: number;
  height: number;

  constructor(width: number, height: number) {
    this.width = width;
    this.height = height;
  }

  equals = (other: ?Dimension) =>
    other && this.width === other.width && this.height === other.height;

  aspectRatio = () => this.width / this.height;
}

/**
 * Helper method to bound a value between a minimum and a maximum
 */
export const boundNumber = (value: number, min: number, max: number) => {
  const newValue = Math.max(value, min);
  return Math.min(newValue, max);
};

/**
 * Shared method used mainly by VideoSection and ShelfVideoSection for detecting the tracking module for player events
 */
export const getVideoTrackingModule = (route: any) => {
  let videoTrackingModule = 'ta_video';
  if (route) {
    if (route.page === 'Home') {
      videoTrackingModule = 'home_video';
    } else if (route.page === 'Profile') {
      videoTrackingModule = 'profile_video';
    }
  }
  return videoTrackingModule;
};
